import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { getTerms } from '@/lib/api/terms/get-terms'
import { getDomains } from '@/lib/api/terms/get-term-extras'
import { getConcepts } from '@/lib/api/concepts/get-concepts'
import { getDocumentTerms } from '@/lib/api/documents/get-documents'
import type { Term } from '@/types/terms'

/** Whole-layer payloads never change during a session; fetch once, keep. */
const STATIC = { staleTime: Infinity, gcTime: Infinity } as const

export function useTerms(collectionId: number | null) {
  return useQuery({
    queryKey: ['terms', collectionId],
    queryFn: () => getTerms(collectionId),
    ...STATIC,
  })
}

export function useDomains(collectionId: number | null) {
  return useQuery({
    queryKey: ['domains', collectionId],
    queryFn: () => getDomains(collectionId),
    ...STATIC,
  })
}

export function useConcepts(collectionId: number | null) {
  return useQuery({
    queryKey: ['concepts', collectionId],
    queryFn: () => getConcepts(collectionId),
    ...STATIC,
  })
}

/**
 * Ids of the terms the selected documents contribute — the document filter.
 * `null` while there is no selection (or any of it is still loading) means
 * "no restriction", so the lists never blink empty or half-filtered on the
 * way in.
 *
 * Same query key per document as the text pane, so opening a document and
 * then filtering by it costs one request, not two.
 */
export function useDocumentTermIds(documentIds: number[]): Set<number> | null {
  const results = useQueries({
    queries: documentIds.map((id) => ({
      queryKey: ['document-terms', id],
      queryFn: () => getDocumentTerms(id),
    })),
  })
  const ready = documentIds.length > 0 && results.every((r) => r.data)
  // useQueries hands back a fresh array each render; key the memo on the data.
  const datas = results.map((r) => r.data)
  return useMemo(
    () => (ready ? new Set(datas.flatMap((d) => (d ?? []).map((t) => t.term_id))) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, ...datas],
  )
}

export type SortKey = 'freq' | 'docs' | 'alpha'

export interface TermFilters {
  q: string
  lang: string
  domain: string
  abbrOnly: boolean
  /** Only these term ids (the document filter); null = every term. */
  termIds?: Set<number> | null
  sort: SortKey
}

/**
 * Rank a match so the most useful hits float up: exact, then prefix, then
 * word-start, then anywhere. Without this a search for "ai" buries the actual
 * term under every word containing those letters.
 */
function matchRank(value: string, q: string): number {
  if (!q) return 0
  const v = value.toLowerCase()
  if (v === q) return 0
  if (v.startsWith(q)) return 1
  if (v.includes(` ${q}`)) return 2
  return v.includes(q) ? 3 : -1
}

export function filterTerms(terms: Term[], filters: TermFilters): Term[] {
  const q = filters.q.trim().toLowerCase()
  const out: { t: Term; rank: number }[] = []

  for (const t of terms) {
    if (filters.lang && t.lang !== filters.lang) continue
    if (filters.domain && t.domain !== filters.domain) continue
    if (filters.abbrOnly && t.term_type !== 'abbreviation') continue
    if (filters.termIds && !filters.termIds.has(t.term_id)) continue
    const rank = matchRank(t.value, q)
    if (rank < 0) continue
    out.push({ t, rank })
  }

  const cmp: Record<SortKey, (a: Term, b: Term) => number> = {
    freq: (a, b) => b.freq - a.freq,
    docs: (a, b) => b.doc_count - a.doc_count,
    alpha: (a, b) => a.value.localeCompare(b.value, 'et'),
  }
  const bySort = cmp[filters.sort]
  // Relevance dominates while searching; the chosen sort breaks ties.
  out.sort((a, b) => (q && a.rank !== b.rank ? a.rank - b.rank : bySort(a.t, b.t)))
  return out.map((x) => x.t)
}

export function useFilteredTerms(collectionId: number | null, filters: TermFilters) {
  const termsQuery = useTerms(collectionId)
  const filtered = useMemo(
    () => filterTerms(termsQuery.data ?? [], filters),
    [termsQuery.data, filters],
  )

  return {
    terms: filtered,
    /** Unfiltered layer — the selected term must stay resolvable even when the
     *  active filters would exclude it, so the detail pane does not blank out. */
    all: termsQuery.data ?? [],
    total: termsQuery.data?.length ?? 0,
    isLoading: termsQuery.isLoading,
    /**
     * `error` is only populated once every retry is exhausted, which would
     * leave the user staring at a skeleton for a minute during a backend
     * restart. `failureReason` surfaces the in-flight failure immediately.
     */
    error: termsQuery.error ?? (termsQuery.failureCount > 0 ? termsQuery.failureReason : null),
    refetch: termsQuery.refetch,
  }
}
